import React, { useState } from "react";
import { toast } from "sonner";
import { Store, FileText, ListChecks, User, Lock, Save, Plus, X, MessageSquare, AlertTriangle, Trash2 } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useSettings, Settings as TSettings } from "../context/SettingsContext";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Field } from "../components/kit";

const TABS = [
  { id: "shop", label: "দোকানের তথ্য", icon: Store },
  { id: "memo", label: "মেমো / বিল", icon: FileText },
  { id: "sms", label: "SMS বার্তা", icon: MessageSquare },
  { id: "lists", label: "তালিকা", icon: ListChecks },
  { id: "profile", label: "প্রোফাইল", icon: User },
  { id: "password", label: "পাসওয়ার্ড", icon: Lock },
  { id: "reset", label: "ডেটা রিসেট", icon: AlertTriangle },
];

function ListEditor({ label, items, onChange, testid }: { label: string; items: string[]; onChange: (v: string[]) => void; testid: string }) {
  const [val, setVal] = useState("");
  const add = () => {
    const v = val.trim();
    if (!v) return;
    if (items.includes(v)) return toast.error("এটি আগে থেকেই আছে");
    onChange([...items, v]);
    setVal("");
  };
  return (
    <div data-testid={testid}>
      <p className="text-sm font-medium text-slate-600 mb-2">{label}</p>
      <div className="flex flex-wrap gap-2 mb-2">
        {items.map((it) => (
          <span key={it} className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-full bg-blue-50 text-blue-700" data-testid={`${testid}-chip`}>
            {it}
            <button type="button" onClick={() => onChange(items.filter((x) => x !== it))} className="text-blue-400 hover:text-red-500" aria-label="মুছুন"><X size={14} /></button>
          </span>
        ))}
        {items.length === 0 && <span className="text-sm text-slate-400">কোনো আইটেম নেই</span>}
      </div>
      <div className="flex gap-2">
        <input className="inp flex-1" value={val} onChange={(e) => setVal(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} placeholder="নতুন আইটেম লিখে + চাপুন" data-testid={`${testid}-input`} />
        <button type="button" className="btn btn-ghost px-3" onClick={add} data-testid={`${testid}-add`}><Plus size={18} /></button>
      </div>
    </div>
  );
}

export default function Settings() {
  const { settings, setSettings } = useSettings();
  const { user, refreshUser } = useAuth() as any;
  const [tab, setTab] = useState("shop");
  const [draft, setDraft] = useState<TSettings>(() => JSON.parse(JSON.stringify(settings)));
  const [saving, setSaving] = useState(false);

  const [name, setName] = useState(user?.name || "");
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [resetWord, setResetWord] = useState("");
  const [resetting, setResetting] = useState(false);
  const [resetScopes, setResetScopes] = useState<string[]>(["memos", "payments", "customers", "purchases", "expenses", "stock_logs"]);
  const toggleScope = (id: string) => setResetScopes((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const isOwner = (user?.role || "") === "owner";

  const doReset = async () => {
    if (resetScopes.length === 0) return toast.error("অন্তত একটি অপশন বেছে নিন");
    if (resetWord.trim() !== "রিসেট") return toast.error("নিশ্চিত করতে ঘরে ‘রিসেট’ লিখুন");
    setResetting(true);
    try {
      const { data } = await api.post("/settings/reset-data", { confirm: resetWord.trim(), scopes: resetScopes });
      const d = data?.deleted || {};
      const totalDeleted = Object.values(d).reduce((a: number, b: any) => a + Number(b || 0), 0);
      toast.success(`নির্বাচিত হিসাব রিসেট হয়েছে (${totalDeleted} রেকর্ড মুছে ফেলা হয়েছে)`);
      setResetWord("");
      setTimeout(() => window.location.reload(), 900);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setResetting(false);
    }
  };

  const shop = (k: keyof TSettings["shop"]) => (e: any) => setDraft({ ...draft, shop: { ...draft.shop, [k]: e.target.value } });
  const memo = (k: keyof TSettings["memo"]) => (e: any) => setDraft({ ...draft, memo: { ...draft.memo, [k]: e.target.value } });
  const sms = (k: "sale" | "payment" | "reminder") => (e: any) => setDraft({ ...draft, sms: { ...(draft.sms || { sale: "", payment: "", reminder: "" }), [k]: e.target.value } });

  const saveSettings = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings", draft);
      setSettings(data);
      setDraft(JSON.parse(JSON.stringify(data)));
      toast.success("সেটিংস সংরক্ষণ হয়েছে");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const saveProfile = async () => {
    if (!name.trim()) return toast.error("নাম দিন");
    setSaving(true);
    try {
      await api.put("/auth/profile", { name: name.trim() });
      refreshUser?.();
      toast.success("নাম আপডেট হয়েছে");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const savePassword = async () => {
    if (pw.next.length < 6) return toast.error("নতুন পাসওয়ার্ড কমপক্ষে ৬ অক্ষরের হতে হবে");
    if (pw.next !== pw.confirm) return toast.error("নতুন পাসওয়ার্ড মিলছে না");
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: pw.current, new_password: pw.next });
      setPw({ current: "", next: "", confirm: "" });
      toast.success("পাসওয়ার্ড পরিবর্তন হয়েছে");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="settings-page">
      <PageHeader title="সেটিংস" subtitle="দোকান, মেমো ও তালিকা আপনার মন মতো এডিট করুন" />
      <div className="grid lg:grid-cols-[220px_1fr] gap-4 items-start">
        <div className="card p-2 flex lg:flex-col gap-1 overflow-x-auto">
          {TABS.map((t) => {
            const Icon = t.icon;
            if (t.id === "reset" && !isOwner) return null;
            const danger = t.id === "reset";
            return (
              <button key={t.id} onClick={() => setTab(t.id)} data-testid={`settings-tab-${t.id}`}
                className={`flex items-center gap-2.5 px-3.5 py-2.5 rounded-lg text-[15px] font-medium whitespace-nowrap transition-colors ${tab === t.id ? (danger ? "bg-red-600 text-white" : "bg-blue-600 text-white") : (danger ? "text-red-600 hover:bg-red-50" : "text-slate-600 hover:bg-slate-100")}`}>
                <Icon size={17} /> {t.label}
              </button>
            );
          })}
        </div>

        <div className="card p-5 md:p-6">
          {tab === "shop" && (
            <div className="space-y-4" data-testid="settings-shop">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="দোকানের নাম *"><input className="inp" value={draft.shop.name} onChange={shop("name")} data-testid="shop-name-input" /></Field>
                <Field label="ইংরেজি নাম (মেমোতে ছোট করে)"><input className="inp" value={draft.shop.name_en} onChange={shop("name_en")} data-testid="shop-name-en-input" /></Field>
                <Field label="ঠিকানা" className="sm:col-span-2"><input className="inp" value={draft.shop.address} onChange={shop("address")} data-testid="shop-address-input" /></Field>
                <Field label="স্বত্বাধিকারী"><input className="inp" value={draft.shop.proprietor} onChange={shop("proprietor")} data-testid="shop-proprietor-input" /></Field>
                <Field label="মোবাইল ১"><input className="inp num" value={draft.shop.mobile} onChange={shop("mobile")} data-testid="shop-mobile-input" /></Field>
                <Field label="মোবাইল ২ (ঐচ্ছিক)"><input className="inp num" value={draft.shop.mobile2} onChange={shop("mobile2")} data-testid="shop-mobile2-input" /></Field>
              </div>
              <div className="flex justify-end"><button className="btn btn-primary" disabled={saving} onClick={saveSettings} data-testid="settings-save-button"><Save size={17} /> সংরক্ষণ করুন</button></div>
            </div>
          )}

          {tab === "memo" && (
            <div className="space-y-4" data-testid="settings-memo">
              <Field label="মেমোর শিরোনাম"><input className="inp" value={draft.memo.title} onChange={memo("title")} data-testid="memo-title-input" /></Field>
              <Field label="ফুটার ধন্যবাদ বার্তা"><input className="inp" value={draft.memo.thank_you} onChange={memo("thank_you")} data-testid="memo-thankyou-input" /></Field>
              <Field label="শর্তাবলী / ওয়ারেন্টি নোট"><textarea className="inp min-h-[72px]" value={draft.memo.warranty_note} onChange={memo("warranty_note")} data-testid="memo-warranty-input" /></Field>
              <div className="flex justify-end"><button className="btn btn-primary" disabled={saving} onClick={saveSettings} data-testid="settings-save-button-memo"><Save size={17} /> সংরক্ষণ করুন</button></div>
            </div>
          )}

          {tab === "sms" && (
            <div className="space-y-4" data-testid="settings-sms">
              <div className="rounded-lg bg-blue-50 text-blue-800 text-sm p-3 leading-relaxed">
                বিক্রি, জমা ও বাকির রিমাইন্ডার SMS-এর লেখা এখানে বদলান। নিচের শব্দগুলো SMS পাঠানোর সময় আপনা-আপনি বসে যাবে —{" "}
                <code>{"{shop}"}</code> দোকান, <code>{"{invoice}"}</code> বিল নম্বর, <code>{"{total}"}</code> মোট, <code>{"{paid}"}</code> জমা, <code>{"{due}"}</code> বাকি, <code>{"{amount}"}</code> আদায়, <code>{"{name}"}</code> ক্রেতার নাম।
              </div>
              <Field label="বিক্রির SMS (নতুন মেমো হলে)"><textarea className="inp min-h-[80px]" value={(draft.sms || {} as any).sale || ""} onChange={sms("sale")} data-testid="sms-sale-input" /></Field>
              <Field label="জমা/পেমেন্টের SMS (টাকা আদায় হলে)"><textarea className="inp min-h-[80px]" value={(draft.sms || {} as any).payment || ""} onChange={sms("payment")} data-testid="sms-payment-input" /></Field>
              <Field label="বাকির রিমাইন্ডার SMS"><textarea className="inp min-h-[80px]" value={(draft.sms || {} as any).reminder || ""} onChange={sms("reminder")} data-testid="sms-reminder-input" /></Field>
              <div className="flex justify-end"><button className="btn btn-primary" disabled={saving} onClick={saveSettings} data-testid="settings-save-button-sms"><Save size={17} /> সংরক্ষণ করুন</button></div>
            </div>
          )}

          {tab === "lists" && (
            <div className="space-y-6" data-testid="settings-lists">
              <ListEditor label="পণ্যের ক্যাটাগরি" items={draft.categories} onChange={(v) => setDraft({ ...draft, categories: v })} testid="list-categories" />
              <ListEditor label="একক (Unit)" items={draft.units} onChange={(v) => setDraft({ ...draft, units: v })} testid="list-units" />
              <ListEditor label="পেমেন্ট মাধ্যম" items={draft.pay_methods} onChange={(v) => setDraft({ ...draft, pay_methods: v })} testid="list-pay-methods" />
              <ListEditor label="খরচের খাত" items={draft.expense_categories} onChange={(v) => setDraft({ ...draft, expense_categories: v })} testid="list-expense-categories" />
              <div className="flex justify-end"><button className="btn btn-primary" disabled={saving} onClick={saveSettings} data-testid="settings-save-button-lists"><Save size={17} /> সংরক্ষণ করুন</button></div>
            </div>
          )}

          {tab === "profile" && (
            <div className="space-y-4 max-w-md" data-testid="settings-profile">
              <Field label="ইমেইল"><input className="inp bg-slate-50" value={user?.email || ""} disabled data-testid="profile-email-input" /></Field>
              <Field label="আপনার নাম"><input className="inp" value={name} onChange={(e) => setName(e.target.value)} data-testid="profile-name-input" /></Field>
              <div className="flex justify-end"><button className="btn btn-primary" disabled={saving} onClick={saveProfile} data-testid="profile-save-button"><Save size={17} /> নাম সংরক্ষণ</button></div>
            </div>
          )}

          {tab === "password" && (
            <div className="space-y-4 max-w-md" data-testid="settings-password">
              <Field label="বর্তমান পাসওয়ার্ড"><input className="inp" type="password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} data-testid="password-current-input" /></Field>
              <Field label="নতুন পাসওয়ার্ড"><input className="inp" type="password" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} data-testid="password-new-input" /></Field>
              <Field label="নতুন পাসওয়ার্ড আবার"><input className="inp" type="password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} data-testid="password-confirm-input" /></Field>
              <div className="flex justify-end"><button className="btn btn-primary" disabled={saving} onClick={savePassword} data-testid="password-save-button"><Lock size={17} /> পাসওয়ার্ড পরিবর্তন</button></div>
            </div>
          )}

          {tab === "reset" && isOwner && (
            <div className="space-y-5 max-w-xl" data-testid="settings-reset">
              <div className="flex items-start gap-3 rounded-lg border-2 border-red-200 bg-red-50 p-4">
                <AlertTriangle className="text-red-600 shrink-0 mt-0.5" size={22} />
                <div className="text-sm text-red-800 leading-relaxed">
                  <p className="font-bold text-[15px] mb-1">সতর্কতা — এই কাজটি ফেরানো যাবে না</p>
                  <p>রিসেট করলে নিচের সব হিসাব <b>০</b> হয়ে যাবে। এটি <b>শুধু হিসাব</b> মুছবে — আপনার পণ্য তালিকা ও দাম অক্ষত থাকবে।</p>
                </div>
              </div>

              <div className="rounded-lg bg-slate-50 p-4 text-sm text-slate-700">
                <p className="font-semibold text-slate-800 mb-3">কোন কোন হিসাব রিসেট হবে বেছে নিন:</p>
                <div className="space-y-2">
                  {[
                    { id: "memos", label: "বিক্রি / মেমো তালিকা", desc: "সব মেমো ও বিক্রির রেকর্ড মুছবে" },
                    { id: "payments", label: "টাকা আদায়ের রেকর্ড", desc: "সব আদায়/কালেকশন মুছবে" },
                    { id: "customers", label: "ক্রেতাদের বাকি ও হিসাব ০", desc: "বাকি, মোট কেনা ও পরিশোধ ০ (নাম থাকবে)" },
                    { id: "purchases", label: "ক্রয় ও কোম্পানির দেনা", desc: "ক্রয় এন্ট্রি + কোম্পানির দেনা ০ (নাম থাকবে)" },
                    { id: "expenses", label: "খরচ / হিসাব", desc: "সব খরচের রেকর্ড মুছবে" },
                    { id: "stock_logs", label: "স্টক লগ ও দিন-ক্লোজ রিপোর্ট", desc: "স্টক মুভমেন্ট লগ ও পুরনো রিপোর্ট মুছবে" },
                  ].map((sc) => (
                    <label key={sc.id} className="flex items-start gap-3 p-2.5 rounded-lg bg-white border border-slate-200 cursor-pointer hover:border-red-300" data-testid={`reset-scope-${sc.id}`}>
                      <input type="checkbox" checked={resetScopes.includes(sc.id)} onChange={() => toggleScope(sc.id)} className="mt-1 w-4 h-4 accent-red-600" data-testid={`reset-scope-${sc.id}-checkbox`} />
                      <span className="leading-tight">
                        <span className="block font-medium text-slate-800">{sc.label}</span>
                        <span className="block text-xs text-slate-500">{sc.desc}</span>
                      </span>
                    </label>
                  ))}
                </div>
                <p className="mt-3 text-emerald-700 font-medium">✓ পণ্য তালিকা, দাম ও স্টক পরিমাণ সবসময় অক্ষত থাকবে।</p>
              </div>

              <div>
                <p className="text-sm font-medium text-slate-600 mb-2">নিশ্চিত করতে নিচের ঘরে <b className="text-red-600">রিসেট</b> লিখুন</p>
                <input className="inp" value={resetWord} onChange={(e) => setResetWord(e.target.value)} placeholder="রিসেট" data-testid="reset-confirm-input" />
              </div>

              <button
                className="btn w-full justify-center bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                disabled={resetting || resetWord.trim() !== "রিসেট" || resetScopes.length === 0}
                onClick={doReset}
                data-testid="reset-data-button"
              >
                <Trash2 size={17} /> {resetting ? "রিসেট হচ্ছে..." : "নির্বাচিত হিসাব রিসেট করুন"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
